<template>
    <div class="card">
        <div class="card-header">
            <h2 class="card-title fw-bolder">{{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.title') }}</h2>
            <div class="card-toolbar">
                <el-switch
                    v-model="paragraphPreview"
                    size="large"
                    :active-text="$t('pages.module.eTep.question.save.questionType.paragraph.paragraphCode')"
                    :inactive-text="$t('pages.module.eTep.question.save.questionType.paragraph.paragraphPreview')"
                />
            </div>
        </div>
        <div class="card-body d-flex flex-column p-9">
            <div class="row" v-if="data.contents?.length">
                <div v-if="!paragraphPreview" class="col-md-12">
                    <div v-for="(content, contentIndex) in data.contents" :key="contentIndex">
                        <span class="text-gray-800 fs-4 fw-bolder">
                            {{ sprintf($t('pages.module.eTep.question.save.questionType.paragraphDragFill.titlePattern.'+ content.type ), [(contentIndex + 1)]) }}
                        </span>
                        <div v-if="content.type === 'paragraph'" v-html="content.content"></div>
                        <div v-if="content.type === 'drag_content'" v-html="answers.find((answer) => answer.id === correctAnswer[content.hash] )?.data.content" class="border border-gray-300 p-3 my-3 pb-0 rounded"></div>
                    </div>
                </div>
                <div v-else class="col-md-12" v-for="(content, contentIndex) in data.contents" :key="contentIndex" :class="[(contentIndex > 0 ? 'mt-md-20' : 'mt-md-0'), contentIndex !== 0 && 'mt-20' ]">
                    <div class="mb-2 d-flex justify-content-between align-items-center">
                        <div class="text-gray-800 fs-4 fw-bolder">
                            {{ sprintf($t('pages.module.eTep.question.save.questionType.paragraphDragFill.titlePattern.'+ content.type ), [(contentIndex + 1)]) }}
                        </div>
                        <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteContent(content, contentIndex)">
                            <template #reference>
                                <button class="btn btn-danger btn-sm" type="button"><i class="bi bi-trash"/> Kaldır</button>
                            </template>
                        </el-popconfirm>
                    </div>
                    <div class="border border-gray-300 pt-5 px-3 pb-0 rounded">
                        <div class="fv-row mb-1" v-if="content.type === 'paragraph'">
                            <el-form-item :prop="'data.data.contents[' + contentIndex + '].content'" :rules="$validation.getMessage(['required'])">
                                <CustomCkeditor :model="content.content" @updateModel="content.content = $event"></CustomCkeditor>
                            </el-form-item>
                        </div>
                        <div class="fv-row mb-1" v-if="content.type === 'drag_content'">
                            <el-form-item v-if="answers?.length" :rules="$validation.getMessage(['required'])">
                                <el-select v-model="correctAnswer[content.hash]" class="w-100">
                                    <el-option v-for="(answer, answerIndex) in answers.filter(a => a.id)" :value="answer.id" :label="answer.data.content.replace(/<\/?[^>]+(>|$)/g, '').slice(0, 100)" :key="answer.id"></el-option>
                                </el-select>
                            </el-form-item>
                            <div v-else>
                                <p>{{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.noAnswer') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="d-flex flex-column align-items-center justify-content-center py-5">
                <p class="text-gray-600 text-center">{{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.paragraphAddContent') }}</p>
                <div class="d-flex flex-column flex-md-row gap-3 align-items-center justify-content-center">
                    <button class="btn btn-flex btn-primary" v-on:click="addContent('paragraph')" type="button">
                        <span class="svg-icon svg-icon-2">
                            <inline-svg src="/media/icons/duotune/arrows/arr075.svg"/>
                        </span>
                        {{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.paragraphAdd') }}
                    </button>
                    <button class="btn btn-flex btn-primary" v-on:click="addContent('drag_content')" type="button">
                        <span class="svg-icon svg-icon-2">
                            <inline-svg src="/media/icons/duotune/arrows/arr075.svg"/>
                        </span>
                        {{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.dragContentAdd') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="card-footer" v-if="data.contents?.length">
            <div class="d-flex flex-column flex-md-row gap-3 align-items-start justify-content-start">
                <button class="btn btn-flex btn-primary" v-on:click="addContent('paragraph')" type="button">
                        <span class="svg-icon svg-icon-2">
                            <inline-svg src="/media/icons/duotune/arrows/arr075.svg"/>
                        </span>
                    {{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.paragraphAdd') }}
                </button>
                <button class="btn btn-flex btn-primary" v-on:click="addContent('drag_content')" type="button">
                        <span class="svg-icon svg-icon-2">
                            <inline-svg src="/media/icons/duotune/arrows/arr075.svg"/>
                        </span>
                    {{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.dragContentAdd') }}
                </button>
            </div>
        </div>
    </div>
    <div class="card mt-7" v-if="updateStatus">
        <div class="card-header">
            <h2 class="card-title fw-bolder">{{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.answer.title') }}</h2>
        </div>
        <div class="card-body">
            <div class="row" v-if="answers?.length">
                <div v-for="(answer, answerIndex) in answers" :key="answerIndex" class="col-md-12" :class="[(answerIndex > 0 ? 'mt-md-4' : 'mt-md-0'), answerIndex !== 0 && 'mt-4' ]">
                    <div class="mb-2 d-flex justify-content-between align-items-center">
                        <div class="text-gray-800 fs-4 fw-bolder">
                            {{ sprintf($t('pages.module.eTep.question.save.questionType.paragraphDragFill.answer.titlePattern'), [(answerIndex + 1)]) }}
                        </div>
                        <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteAnswer(answer, answerIndex)">
                            <template #reference>
                                <button class="btn btn-danger btn-sm" type="button"><i class="bi bi-trash"/> Kaldır</button>
                            </template>
                        </el-popconfirm>
                    </div>
                    <div class="border border-gray-300 pt-5 px-3 pb-0 rounded">
                        <div class="fv-row mb-1">
                            <el-form-item :prop="'data.answers[' + answerIndex + '].data.content'" :rules="$validation.getMessage(['required'])">
                                <CustomCkeditor :model="answer.data.content" @updateModel="answer.data.content = $event"></CustomCkeditor>
                            </el-form-item>
                            <el-form-item :rules="$validation.getMessage(['required'])">
                                <el-input-number v-model="answer.sort" :min="1"/>
                            </el-form-item>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="d-flex flex-column align-items-center justify-content-center py-5">
                <p class="text-gray-600 text-center">{{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.answer.answerAddContent') }}</p>
                <button class="btn btn-flex btn-primary me-2" v-on:click="addAnswer()" type="button">
                    <span class="svg-icon svg-icon-2">
                        <inline-svg src="/media/icons/duotune/arrows/arr075.svg"/>
                    </span>
                    {{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.answer.add') }}
                </button>
            </div>
        </div>
        <div class="card-footer" v-if="answers?.length">
            <button class="btn btn-flex btn-primary me-2" v-on:click="addAnswer()" type="button">
                <span class="svg-icon svg-icon-2">
                    <inline-svg src="/media/icons/duotune/arrows/arr075.svg"/>
                </span>
                {{ $t('pages.module.eTep.question.save.questionType.paragraphDragFill.answer.add') }}
            </button>
        </div>
    </div>
</template>

<script>
import CustomCkeditor from "@/components/custom-ckeditor/index.vue";
import md5 from 'md5'

export default {
    name: "ParagraphDragFill",
    props: {
        data: Object,
        answers: Array,
        updateStatus: Boolean,
        correctAnswer: Array
    },
    data() {
        return {
            paragraphPreview: true,
        }
    },
    components: {
        CustomCkeditor
    },
    methods: {
        addContent(type) {
            if (type === 'paragraph') {
                this.data.contents.push({
                    "type": type,
                    "content": ""
                })
            } else if (type === 'drag_content') {
                this.data.contents.push({
                    "type": type,
                    "hash": this.generateHash(),
                })
            }
        },
        deleteContent(paragraph, paragraphIndex) {
            this.data.contents.splice(paragraphIndex, 1);
        },
        generateHash() {
            const timestamp = Date.now().toString()
            const random = Math.random().toString(36).substring(2, 10)
            const input = `${timestamp}-${random}`
            this.paragraphPreview = true
            return md5(input);
        },
        addAnswer() {
            let answerLength = this.answers.length;
            this.answers.push({
                data: {
                    content: ''
                },
                sort: answerLength !== 0 ? (this.answers[answerLength - 1].sort + 1) : 1,
            })
        },
        deleteAnswer(value, index) {
            if (value.id) {
                this.$store.dispatch("module/etep/question/answer/delete", {
                    id: [value.id]
                }).then((successDeleted) => {
                    if (successDeleted) {
                        this.$emit('load-question', true)
                    }
                });
            } else {
                this.answers.splice(index, 1);
            }
        }
    }
};
</script>
